import { useEffect, useMemo, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import type { WorkspacesGateway } from '../application/workspacesGateway';
import type { AuthenticatedIdentity } from '../domain/access';
import type {
  ConversationMessage,
  DealBoardBucket,
  DealBoardInviteCandidate,
  DealBoardItem,
  DealBoardMember,
  WorkspaceConversation,
  WorkspaceSummary,
  WorkspaceVector,
} from '../domain/workspaces';
import styles from './WorkSpacesScreen.module.css';

interface WorkSpacesScreenProps {
  readonly gateway: WorkspacesGateway;
  readonly navigateToAccess?: () => void;
  readonly navigateToDashboard?: () => void;
  readonly navigateToInventory?: () => void;
}

type GroupSection = 'overview' | 'members' | 'media' | 'documents';

interface ScreenState {
  readonly identity: AuthenticatedIdentity | null;
  readonly workspaces: readonly WorkspaceSummary[];
  readonly selectedWorkspaceId: string | null;
  readonly vectors: readonly WorkspaceVector[];
  readonly conversations: readonly WorkspaceConversation[];
  readonly selectedConversationId: string | null;
  readonly messages: readonly ConversationMessage[];
  readonly loading: boolean;
  readonly contextLoading: boolean;
  readonly messageLoading: boolean;
  readonly sending: boolean;
  readonly error: string | null;
}

const bucketLabels: Record<DealBoardBucket, string> = {
  sent: 'Enviadas',
  received: 'Recibidas',
  authorized: 'Autorizadas',
  customers: 'Customers',
};

const bucketDescriptions: Record<DealBoardBucket, string> = {
  sent: 'Solicitudes que tú iniciaste',
  received: 'Solicitudes que esperan tu decisión',
  authorized: 'Colaboraciones que ya tienen autoridad',
  customers: 'Relaciones activas vinculadas a Customers',
};

function defaultNavigateToAccess(): void {
  window.location.replace('/');
}

function defaultNavigateToDashboard(): void {
  window.location.assign('/dashboard');
}

function defaultNavigateToInventory(): void {
  window.location.assign('/inventory');
}

function formatPrice(vector: WorkspaceVector): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: vector.currency,
    maximumFractionDigits: 0,
  }).format(vector.price);
}

function timeLabel(value: string | null): string {
  if (value === null) return '';
  return new Date(value).toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(-2)
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function itemStatus(item: DealBoardItem): string {
  if (item.bucket === 'authorized') {
    return item.provenance === 'invited_by_listing_side'
      ? 'Invitación activa'
      : 'Autorización activa';
  }
  if (item.bucket === 'customers') return item.customerName ?? 'Customer';
  const labels: Record<string, string> = {
    pending: 'Pendiente',
    authorized: 'Autorizada',
    ignored: 'Ignorada',
    declined: 'Rechazada',
    revoked: 'Revocada',
    expired: 'Vencida',
    active: 'Activa',
  };
  return labels[item.status] ?? item.status;
}

function toggleSelection(
  current: readonly string[],
  value: string,
): readonly string[] {
  return current.includes(value)
    ? current.filter(item => item !== value)
    : [...current, value];
}

export function WorkSpacesScreen({
  gateway,
  navigateToAccess = defaultNavigateToAccess,
  navigateToDashboard = defaultNavigateToDashboard,
  navigateToInventory = defaultNavigateToInventory,
}: WorkSpacesScreenProps): React.JSX.Element {
  const [chatQuery, setChatQuery] = useState('');
  const [threadQuery, setThreadQuery] = useState('');
  const [threadSearchOpen, setThreadSearchOpen] = useState(false);
  const [pluginTrayOpen, setPluginTrayOpen] = useState(false);
  const [draftMessage, setDraftMessage] = useState('');
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [groupSection, setGroupSection] = useState<GroupSection>('overview');
  const [bucket, setBucket] = useState<DealBoardBucket>('authorized');
  const [bucketItems, setBucketItems] = useState<readonly DealBoardItem[]>([]);
  const [bucketLoading, setBucketLoading] = useState(true);
  const [selectedDealItemId, setSelectedDealItemId] = useState<string | null>(null);
  const [members, setMembers] = useState<readonly DealBoardMember[]>([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteCandidates, setInviteCandidates] = useState<readonly DealBoardInviteCandidate[]>([]);
  const [selectedInviteAgents, setSelectedInviteAgents] = useState<readonly string[]>([]);
  const [selectedInviteListings, setSelectedInviteListings] = useState<readonly string[]>([]);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [decisionBusy, setDecisionBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [state, setState] = useState<ScreenState>({
    identity: null,
    workspaces: [],
    selectedWorkspaceId: null,
    vectors: [],
    conversations: [],
    selectedConversationId: null,
    messages: [],
    loading: true,
    contextLoading: false,
    messageLoading: false,
    sending: false,
    error: null,
  });
  const messageEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    void gateway.resolveCurrentIdentity(controller.signal).then(async identityResult => {
      if (!active) return;
      if (!identityResult.ok) {
        navigateToAccess();
        return;
      }

      const overview = await gateway.loadOverview(controller.signal);
      if (controller.signal.aborted) return;
      if (!overview.ok) {
        setState(current => ({
          ...current,
          identity: identityResult.identity,
          loading: false,
          error: overview.message,
        }));
        return;
      }

      setState(current => ({
        ...current,
        identity: identityResult.identity,
        workspaces: overview.workspaces,
        selectedWorkspaceId: overview.workspaces[0]?.workspaceId ?? null,
        loading: false,
        contextLoading: overview.workspaces.length > 0,
        error: null,
      }));
    }).catch((error: unknown) => {
      if (!controller.signal.aborted && !(error instanceof DOMException && error.name === 'AbortError')) {
        navigateToAccess();
      }
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [gateway, navigateToAccess]);

  useEffect(() => {
    if (state.identity === null) return;
    const controller = new AbortController();
    let active = true;

    void gateway.loadBucket(bucket, null, controller.signal).then(result => {
      if (!active) return;
      setBucketLoading(false);
      if (!result.ok) {
        setState(current => ({ ...current, error: result.message }));
        return;
      }

      setBucketItems(result.items);
      const selected = result.items[0] ?? null;
      setSelectedDealItemId(selected?.sourceId ?? null);
      if (selected !== null) {
        setState(current => ({
          ...current,
          selectedWorkspaceId: selected.workspaceId,
          vectors: [],
          conversations: [],
          selectedConversationId: null,
          messages: [],
          contextLoading: true,
          messageLoading: false,
          error: null,
        }));
      }
    }).catch((error: unknown) => {
      if (!controller.signal.aborted && !(error instanceof DOMException && error.name === 'AbortError')) {
        setBucketLoading(false);
        setState(current => ({ ...current, error: 'No fue posible cargar esta bandeja de DealBoard.' }));
      }
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [bucket, gateway, state.identity]);

  useEffect(() => {
    if (state.identity === null || state.selectedWorkspaceId === null) return;
    const controller = new AbortController();
    let active = true;
    const workspaceId = state.selectedWorkspaceId;

    void Promise.all([
      gateway.loadVectors(workspaceId, controller.signal),
      gateway.loadConversations(workspaceId, controller.signal),
    ]).then(([vectorsResult, conversationsResult]) => {
      if (!active) return;
      if (!vectorsResult.ok || !conversationsResult.ok) {
        setState(current => ({
          ...current,
          contextLoading: false,
          error: !vectorsResult.ok
            ? vectorsResult.message
            : conversationsResult.ok
              ? null
              : conversationsResult.message,
        }));
        return;
      }

      const preferredConversation = bucketItems.find(item => item.sourceId === selectedDealItemId)?.conversationId;
      const conversationId =
        preferredConversation !== null &&
        preferredConversation !== undefined &&
        conversationsResult.conversations.some(conversation => conversation.conversationId === preferredConversation)
          ? preferredConversation
          : (conversationsResult.conversations[0]?.conversationId ?? null);

      setState(current => ({
        ...current,
        vectors: vectorsResult.vectors,
        conversations: conversationsResult.conversations,
        selectedConversationId: conversationId,
        messages: [],
        contextLoading: false,
        messageLoading: conversationId !== null,
        error: null,
      }));
    }).catch((error: unknown) => {
      if (!controller.signal.aborted && !(error instanceof DOMException && error.name === 'AbortError')) {
        setState(current => ({ ...current, contextLoading: false, error: 'No fue posible cargar esta relación.' }));
      }
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [bucketItems, gateway, selectedDealItemId, state.identity, state.selectedWorkspaceId]);

  useEffect(() => {
    if (state.identity === null || state.selectedConversationId === null) return;
    const controller = new AbortController();
    let active = true;
    const conversationId = state.selectedConversationId;

    void gateway.loadMessages(conversationId, controller.signal).then(result => {
      if (!active) return;
      if (!result.ok) {
        setState(current => ({ ...current, messageLoading: false, error: result.message }));
        return;
      }
      setState(current => ({ ...current, messages: result.messages, messageLoading: false, error: null }));
    }).catch((error: unknown) => {
      if (!controller.signal.aborted && !(error instanceof DOMException && error.name === 'AbortError')) {
        setState(current => ({ ...current, messageLoading: false, error: 'No fue posible cargar el hilo.' }));
      }
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [gateway, state.identity, state.selectedConversationId]);

  useEffect(() => {
    const target = messageEndRef.current;
    if (target !== null && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'end' });
    }
  }, [state.messages, state.selectedConversationId]);

  const selectedWorkspace = state.workspaces.find(item => item.workspaceId === state.selectedWorkspaceId) ?? null;
  const selectedConversation = state.conversations.find(item => item.conversationId === state.selectedConversationId) ?? null;
  const selectedDealItem = bucketItems.find(item => item.sourceId === selectedDealItemId) ?? null;
  const selectedVector =
    state.vectors.find(vector => vector.vectorId === selectedDealItem?.propertyVectorId) ??
    state.vectors[0] ??
    null;

  const filteredConversations = useMemo(() => {
    const query = chatQuery.trim().toLocaleLowerCase('es-MX');
    if (query === '') return state.conversations;
    return state.conversations.filter(conversation =>
      [conversation.title, conversation.lastMessage]
        .filter((value): value is string => value !== null)
        .some(value => value.toLocaleLowerCase('es-MX').includes(query)),
    );
  }, [chatQuery, state.conversations]);

  const visibleMessages = useMemo(() => {
    const query = threadQuery.trim().toLocaleLowerCase('es-MX');
    if (query === '') return state.messages;
    return state.messages.filter(message =>
      [message.body, message.senderName].some(value => value.toLocaleLowerCase('es-MX').includes(query)),
    );
  }, [state.messages, threadQuery]);

  const mediaMessages = state.messages.filter(message => ['image', 'video', 'media'].includes(message.messageType));
  const documentMessages = state.messages.filter(message => ['document', 'file'].includes(message.messageType));

  function selectWorkspace(workspaceId: string): void {
    setState(current => ({
      ...current,
      selectedWorkspaceId: workspaceId,
      vectors: [],
      conversations: [],
      selectedConversationId: null,
      messages: [],
      contextLoading: true,
      messageLoading: false,
      error: null,
    }));
    setShowGroupInfo(false);
    setThreadQuery('');
  }

  function selectConversation(conversationId: string): void {
    setState(current => ({
      ...current,
      selectedConversationId: conversationId,
      messages: [],
      messageLoading: true,
      error: null,
    }));
    setShowGroupInfo(false);
    setPluginTrayOpen(false);
    setThreadSearchOpen(false);
    setThreadQuery('');
  }

  function selectBucket(nextBucket: DealBoardBucket): void {
    setBucketLoading(true);
    setBucket(nextBucket);
    setSelectedDealItemId(null);
    setInviteOpen(false);
  }

  function selectDealItem(item: DealBoardItem): void {
    setSelectedDealItemId(item.sourceId);
    selectWorkspace(item.workspaceId);
  }

  async function refreshBucket(): Promise<void> {
    const result = await gateway.loadBucket(bucket, null);
    if (!result.ok) {
      setState(current => ({ ...current, error: result.message }));
      return;
    }
    setBucketItems(result.items);
  }

  async function decideAuthorization(
    item: DealBoardItem,
    decision: 'authorized' | 'ignored' | 'declined',
  ): Promise<void> {
    setDecisionBusy(item.sourceId);
    const result = await gateway.respondAuthorization(item.sourceId, decision);
    setDecisionBusy(null);
    if (!result.ok) {
      setState(current => ({ ...current, error: result.message }));
      return;
    }
    setNotice(
      decision === 'authorized'
        ? 'Autorización concedida y persistida.'
        : decision === 'ignored'
          ? 'Solicitud ignorada.'
          : 'Solicitud rechazada.',
    );
    await refreshBucket();
  }

  async function openGroupInfo(): Promise<void> {
    setShowGroupInfo(true);
    setGroupSection('overview');
    if (state.selectedConversationId === null) return;
    const result = await gateway.loadMembers(state.selectedConversationId);
    if (!result.ok) {
      setState(current => ({ ...current, error: result.message }));
      return;
    }
    setMembers(result.members);
  }

  async function openInvitation(): Promise<void> {
    const listings = Array.from(new Set(bucketItems.filter(item => item.bucket === 'authorized').map(item => item.listingId)));
    setSelectedInviteListings(listings.slice(0, 1));
    setSelectedInviteAgents([]);
    setInviteBusy(true);
    const result = await gateway.loadInviteCandidates();
    setInviteBusy(false);
    if (!result.ok) {
      setState(current => ({ ...current, error: result.message }));
      return;
    }
    setInviteCandidates(result.candidates);
    setInviteOpen(true);
  }

  async function submitInvitation(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (selectedInviteListings.length === 0 || selectedInviteAgents.length === 0) return;
    setInviteBusy(true);
    const result = await gateway.createInvitation(selectedInviteListings, selectedInviteAgents);
    setInviteBusy(false);
    if (!result.ok) {
      setState(current => ({ ...current, error: result.message }));
      return;
    }
    setInviteOpen(false);
    setNotice(
      `${String(selectedInviteListings.length)} propiedad(es) × ${String(selectedInviteAgents.length)} profesional(es): invitación persistida.`,
    );
  }

  async function sendBody(body: string): Promise<void> {
    if (body.trim() === '' || state.selectedConversationId === null || state.sending) return;
    const conversationId = state.selectedConversationId;
    setState(current => ({ ...current, sending: true, error: null }));
    const sent = await gateway.sendMessage(conversationId, body.trim());
    if (!sent.ok) {
      setState(current => ({ ...current, sending: false, error: sent.message }));
      return;
    }
    const refreshed = await gateway.loadMessages(conversationId);
    if (!refreshed.ok) {
      setState(current => ({ ...current, sending: false, error: refreshed.message }));
      return;
    }
    setDraftMessage('');
    setPluginTrayOpen(false);
    setState(current => ({ ...current, sending: false, messages: refreshed.messages, error: null }));
  }

  async function sendMessage(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    await sendBody(draftMessage);
  }

  async function shareVector(): Promise<void> {
    if (selectedVector === null) return;
    await sendBody(
      `🏠 ${selectedVector.title}\n${formatPrice(selectedVector)} · ${selectedVector.city}, ${selectedVector.state}\nProperty Vector: ${selectedVector.vectorId}`,
    );
  }

  async function shareBusinessCard(): Promise<void> {
    let availableMembers = members;
    if (availableMembers.length === 0 && state.selectedConversationId !== null) {
      const result = await gateway.loadMembers(state.selectedConversationId);
      if (!result.ok) {
        setState(current => ({ ...current, error: result.message }));
        return;
      }
      availableMembers = result.members;
      setMembers(result.members);
    }
    const mine = availableMembers.find(member => member.isMe);
    if (mine === undefined) return;
    await sendBody(
      `👤 BusinessCard\n${mine.publicName}\n${mine.agencyName ?? 'Profesional independiente'} · ${mine.professionalRole ?? 'Profesional'}\n${mine.licenseNumber ?? 'Registro no publicado'}`,
    );
  }

  async function shareCustomer(): Promise<void> {
    if (selectedVector === null) return;
    await sendBody(`◇ Customer\n${selectedVector.clientName}\nRelación vinculada a ${selectedVector.title}`);
  }

  return (
    <main className={styles.shell}>
      <header className={styles.appBar}>
        <div className={styles.appBarLeft}>
          <button type="button" className={styles.iconButton} onClick={navigateToDashboard} aria-label="Volver al Dashboard">←</button>
          <button className={styles.brand} type="button" onClick={navigateToDashboard}>
            <strong>Alpha•MLS</strong><span>GLOBAL</span>
          </button>
        </div>
        <div className={styles.appActions}>
          <div className={styles.role}><span>ROL ACTIVO</span><strong>{state.identity?.displayName ?? 'Sesión protegida'}</strong></div>
          <button className={styles.iconButton} type="button" aria-label="Volver al Dashboard" onClick={navigateToDashboard}>⌂</button>
        </div>
      </header>

      <div className={styles.surfaceHeader}>
        <div><span className={styles.kicker}>WORKSPACES</span><strong>DealBoard</strong></div>
        <nav className={styles.primaryTabs} aria-label="WorkSpaces">
          <button type="button" aria-pressed="true">DealBoard</button>
          <button type="button" aria-pressed="false" onClick={navigateToInventory}>Inventario MLS</button>
        </nav>
        <div className={styles.headerTools}>
          <button type="button" className={styles.iconButtonLight} aria-label="Abrir Inventario MLS" onClick={navigateToInventory}>⌖</button>
        </div>
      </div>

      {state.error !== null ? <div className={styles.error} role="alert">{state.error}</div> : null}
      {notice !== null ? (
        <div className={styles.notice} role="status"><span>{notice}</span><button type="button" onClick={() => setNotice(null)}>×</button></div>
      ) : null}
      {state.loading ? <div className={styles.loading} role="status"><span />Leyendo relaciones y operaciones…</div> : null}

      {!state.loading ? (
        <section className={styles.dealboard}>
          <div className={styles.dealboardBuckets}>
            {(Object.keys(bucketLabels) as DealBoardBucket[]).map(entry => (
              <button
                type="button"
                key={entry}
                aria-label={bucketLabels[entry]}
                className={bucket === entry ? styles.bucketActive : undefined}
                aria-current={bucket === entry ? 'page' : undefined}
                onClick={() => selectBucket(entry)}
              >
                <span>{bucketLabels[entry]}</span>
                {bucket === entry && !bucketLoading ? <b>{String(bucketItems.length)}</b> : null}
              </button>
            ))}
          </div>

          <div className={styles.messagingLayout}>
            <aside className={styles.activityRail}>
              <div className={styles.railHeader}>
                <div><small>DEALBOARD</small><h2>{bucketLabels[bucket]}</h2><p>{bucketDescriptions[bucket]}</p></div>
                {bucket === 'authorized' ? (
                  <button
                    type="button"
                    aria-label="Hacer invitación"
                    className={styles.inviteIcon}
                    disabled={bucketItems.length === 0 || inviteBusy}
                    onClick={() => void openInvitation()}
                  >＋</button>
                ) : null}
              </div>

              {bucketLoading ? <div className={styles.railLoading}><span />Cargando…</div> : null}
              {!bucketLoading && bucketItems.length === 0 ? (
                <div className={styles.railEmpty}><span>◇</span><strong>Sin movimientos aquí todavía</strong><p>{bucketDescriptions[bucket]}.</p></div>
              ) : null}

              {!bucketLoading && bucketItems.map(item => (
                <div key={`${item.bucket}:${item.sourceId}`} className={selectedDealItemId === item.sourceId ? styles.activityActive : styles.activity}>
                  <button type="button" className={styles.activityMain} onClick={() => selectDealItem(item)}>
                    <span className={styles.eventIcon}>{item.bucket === 'customers' ? '◇' : item.bucket === 'received' ? '↓' : '↑'}</span>
                    <span className={styles.activityCopy}>
                      <strong>{item.title}</strong>
                      <small>{item.counterpartyName ?? item.customerName ?? 'Relación operativa'}</small>
                      <em>{itemStatus(item)}</em>
                    </span>
                    <time>{timeLabel(item.updatedAt)}</time>
                  </button>
                  {bucket === 'received' && item.status === 'pending' ? (
                    <div className={styles.authorizationActions}>
                      <button type="button" disabled={decisionBusy !== null} onClick={() => void decideAuthorization(item, 'authorized')}>Autorizar</button>
                      <button type="button" disabled={decisionBusy !== null} onClick={() => void decideAuthorization(item, 'ignored')}>Ignorar</button>
                      <button type="button" disabled={decisionBusy !== null} onClick={() => void decideAuthorization(item, 'declined')}>Rechazar</button>
                    </div>
                  ) : null}
                </div>
              ))}
            </aside>

            <aside className={styles.chatList}>
              <div className={styles.chatListHeader}>
                <div><small>RELACIÓN</small><strong>{selectedWorkspace?.name ?? 'Selecciona un WorkSpace'}</strong></div>
                <label><span>⌕</span><input value={chatQuery} onChange={event => setChatQuery(event.currentTarget.value)} placeholder="Buscar conversación" aria-label="Buscar conversación" /></label>
              </div>
              {state.contextLoading ? <div className={styles.loadingInline}>Cargando relación…</div> : null}
              {state.vectors.length > 0 ? (
                <div className={styles.vectorStrip}>
                  {state.vectors.map(vector => (
                    <article key={vector.vectorId} className={selectedVector?.vectorId === vector.vectorId ? styles.vectorActive : styles.vector}>
                      <small>{vector.stageName ?? vector.vectorStatus}</small><strong>{vector.title}</strong><span>{formatPrice(vector)} · {vector.clientName}</span>
                    </article>
                  ))}
                </div>
              ) : null}
              <div className={styles.chatRows}>
                {filteredConversations.map(conversation => (
                  <button
                    type="button"
                    key={conversation.conversationId}
                    className={state.selectedConversationId === conversation.conversationId ? styles.chatActive : styles.chat}
                    onClick={() => selectConversation(conversation.conversationId)}
                  >
                    <span className={styles.chatAvatar}>{initials(conversation.title)}</span>
                    <span className={styles.chatCopy}><strong>{conversation.title}</strong><small>{conversation.lastMessage ?? 'Sin mensajes todavía'}</small></span>
                    <span className={styles.chatMeta}><time>{timeLabel(conversation.lastMessageAt)}</time><b>{String(conversation.participantCount)}</b></span>
                  </button>
                ))}
                {!state.contextLoading && filteredConversations.length === 0 ? <div className={styles.chatEmpty}>No hay conversaciones que coincidan.</div> : null}
              </div>
            </aside>

            <section className={styles.thread}>
              {selectedConversation === null ? (
                <div className={styles.threadEmpty}><span>◇</span><h2>Selecciona una conversación</h2><p>Mensajes, Property Vectors y herramientas de colaboración aparecerán aquí.</p></div>
              ) : (
                <>
                  <header className={styles.threadHeader}>
                    <button type="button" className={styles.mobileBack} aria-label="Volver a conversaciones" onClick={() => setState(current => ({ ...current, selectedConversationId: null, messages: [] }))}>←</button>
                    <span className={styles.threadAvatar}>{initials(selectedConversation.title)}</span>
                    <button type="button" className={styles.threadIdentity} onClick={() => void openGroupInfo()}>
                      <strong>{selectedConversation.title}</strong>
                      <small>{String(selectedConversation.participantCount)} participantes · {selectedWorkspace?.name}</small>
                    </button>
                    <div className={styles.threadTools}>
                      <button type="button" aria-label="Buscar en conversación" aria-pressed={threadSearchOpen} onClick={() => setThreadSearchOpen(current => !current)}>⌕</button>
                      <button type="button" aria-label="Información de la conversación" onClick={() => void openGroupInfo()}>⋮</button>
                    </div>
                  </header>

                  {threadSearchOpen ? (
                    <div className={styles.threadSearch}>
                      <span>⌕</span><input autoFocus value={threadQuery} onChange={event => setThreadQuery(event.currentTarget.value)} placeholder="Buscar en esta conversación" aria-label="Buscar en esta conversación" />
                      <button type="button" aria-label="Cerrar búsqueda" onClick={() => { setThreadQuery(''); setThreadSearchOpen(false); }}>×</button>
                    </div>
                  ) : null}

                  <div className={styles.messages} aria-live="polite">
                    {selectedDealItem !== null && selectedDealItem.bucket !== 'customers' ? (
                      <article className={styles.systemCard}><small>AUTHORIZATION</small><strong>{selectedDealItem.title}</strong><span>{itemStatus(selectedDealItem)}</span></article>
                    ) : null}
                    {selectedVector !== null ? (
                      <article className={styles.propertyCardMessage}>
                        <span className={styles.propertyGlyph}>⌂</span>
                        <div><small>PROPERTY VECTOR</small><strong>{selectedVector.title}</strong><span>{formatPrice(selectedVector)} · {selectedVector.city}, {selectedVector.state}</span><em>{selectedVector.clientName} · {selectedVector.stageName ?? selectedVector.vectorStatus}</em></div>
                      </article>
                    ) : null}
                    {state.messageLoading ? <div className={styles.messageLoading}>Abriendo hilo…</div> : null}
                    {visibleMessages.map(message => (
                      <article key={message.messageId} className={message.isMine ? styles.messageMine : styles.messageOther}>
                        {!message.isMine ? <strong>{message.senderName}</strong> : null}<p>{message.body}</p><time dateTime={message.createdAt}>{timeLabel(message.createdAt)} {message.isMine ? '✓✓' : ''}</time>
                      </article>
                    ))}
                    {!state.messageLoading && visibleMessages.length === 0 ? <p className={styles.messageEmpty}>{threadQuery.trim() === '' ? 'Todavía no hay mensajes.' : 'No hay coincidencias en este hilo.'}</p> : null}
                    <div ref={messageEndRef} />
                  </div>

                  {pluginTrayOpen ? (
                    <div className={styles.pluginTray}>
                      <button type="button" disabled={selectedVector === null || state.sending} onClick={() => void shareVector()}><span>⌂</span><strong>Propiedad</strong></button>
                      <button type="button" disabled={state.sending} onClick={() => void shareBusinessCard()}><span>◉</span><strong>BusinessCard</strong></button>
                      <button type="button" disabled={selectedVector === null || state.sending} onClick={() => void shareCustomer()}><span>◇</span><strong>Customer</strong></button>
                      <button type="button" onClick={() => void openGroupInfo()}><span>◎</span><strong>Miembros</strong></button>
                    </div>
                  ) : null}

                  <form className={styles.composer} onSubmit={event => void sendMessage(event)}>
                    <button type="button" aria-label="Abrir herramientas" aria-pressed={pluginTrayOpen} onClick={() => setPluginTrayOpen(current => !current)}>＋</button>
                    <label htmlFor="dealboard-message">Mensaje</label>
                    <textarea id="dealboard-message" rows={1} maxLength={4000} value={draftMessage} disabled={state.sending} onChange={event => setDraftMessage(event.currentTarget.value)} placeholder="Mensaje" />
                    <button type="submit" aria-label="Enviar" disabled={state.sending || draftMessage.trim() === ''}>{state.sending ? '…' : '➤'}</button>
                  </form>
                </>
              )}
            </section>

            {selectedConversation !== null && showGroupInfo ? (
              <aside className={styles.groupInfo}>
                <header><button type="button" aria-label="Cerrar información" onClick={() => setShowGroupInfo(false)}>←</button><strong>Info. de la conversación</strong></header>
                <div className={styles.groupHero}><span>{initials(selectedConversation.title)}</span><h2>{selectedConversation.title}</h2><p>{String(selectedConversation.participantCount)} participantes</p></div>
                <div className={styles.infoActions}>
                  <button type="button" aria-pressed={groupSection === 'members'} onClick={() => setGroupSection('members')}><span>◉</span>Miembros</button>
                  <button type="button" aria-pressed={groupSection === 'media'} onClick={() => setGroupSection('media')}><span>▧</span>Media</button>
                  <button type="button" aria-pressed={groupSection === 'documents'} onClick={() => setGroupSection('documents')}><span>≡</span>Documentos</button>
                </div>
                {groupSection !== 'overview' ? <button type="button" className={styles.infoBack} onClick={() => setGroupSection('overview')}>← Resumen</button> : null}
                {groupSection === 'overview' ? (
                  <><section><h3>WorkSpace</h3><strong>{selectedWorkspace?.name}</strong><p>{[selectedWorkspace?.city, selectedWorkspace?.state].filter(Boolean).join(', ')}</p></section><section><h3>Property Vector activo</h3>{selectedVector !== null ? <><strong>{selectedVector.title}</strong><p>{formatPrice(selectedVector)}</p><small>{selectedVector.stageName ?? selectedVector.vectorStatus}</small></> : <p>Sin Property Vector seleccionado.</p>}</section></>
                ) : null}
                {groupSection === 'members' ? <section><h3>Miembros y BusinessCards</h3>{members.map(member => <article className={styles.memberCard} key={member.agentId}><span className={styles.memberAvatar}>{initials(member.publicName)}</span><div><strong>{member.publicName}{member.isMe ? ' · Tú' : ''}</strong><small>{member.agencyName ?? 'Agency independiente'} · {member.professionalRole ?? 'Profesional'}</small><em>{member.licenseNumber ?? 'Registro no publicado'}</em></div></article>)}</section> : null}
                {groupSection === 'media' ? <section><h3>Media</h3><strong>{String(mediaMessages.length)}</strong><p>elementos multimedia en los mensajes cargados.</p></section> : null}
                {groupSection === 'documents' ? <section><h3>Documentos</h3><strong>{String(documentMessages.length)}</strong><p>documentos en los mensajes cargados.</p></section> : null}
              </aside>
            ) : null}
          </div>
        </section>
      ) : null}

      {inviteOpen ? (
        <div className={styles.inviteBackdrop}>
          <form className={styles.invitePanel} onSubmit={event => void submitInvitation(event)}>
            <header><div><small>AUTORIZADAS</small><h2>Hacer invitación</h2><p>Cada combinación crea un Property Vector independiente y preautorizado.</p></div><button type="button" onClick={() => setInviteOpen(false)}>×</button></header>
            <section>
              <h3>Propiedades autorizadas</h3>
              {Array.from(new Map(bucketItems.map(item => [item.listingId, item])).values()).map(item => (
                <label key={item.listingId}><input type="checkbox" checked={selectedInviteListings.includes(item.listingId)} onChange={() => setSelectedInviteListings(current => toggleSelection(current, item.listingId))} /><span><strong>{item.title}</strong><small>{itemStatus(item)}</small></span></label>
              ))}
            </section>
            <section>
              <h3>Profesionales de RED</h3>
              <div className={styles.inviteGrid}>
                {inviteCandidates.map(candidate => (
                  <label key={candidate.agentId}><input type="checkbox" checked={selectedInviteAgents.includes(candidate.agentId)} onChange={() => setSelectedInviteAgents(current => toggleSelection(current, candidate.agentId))} /><span><strong>{candidate.publicName}</strong><small>{candidate.agencyName ?? 'Agency independiente'} · {candidate.city}</small></span></label>
                ))}
              </div>
            </section>
            <footer><span>{String(selectedInviteListings.length)} propiedad(es) × {String(selectedInviteAgents.length)} profesional(es) = {String(selectedInviteListings.length * selectedInviteAgents.length)} relaciones</span><button type="submit" disabled={inviteBusy || selectedInviteListings.length === 0 || selectedInviteAgents.length === 0}>{inviteBusy ? 'Creando…' : 'Crear invitaciones'}</button></footer>
          </form>
        </div>
      ) : null}
    </main>
  );
}
